/**
 * ListPagePanel — zakładka „Strona" w Kreatorze Listy.
 *
 * Ustawienia całej strony listy (nie pojedynczej kolumny):
 *  - Podstawowe: własny tytuł/opis strony + ile rekordów na stronę.
 *  - Pasek nad listą: które elementy pokazywać (statystyki / wyszukiwarka / pasek filtrów).
 *  - Wygląd paska wyszukiwania i filtrów: styl bloku + pól (tło/tekst/ramka/zaokrąglenie).
 *
 * Wygląd widgetów (`pageConfig.widgetStyle`) przeniesiony do zakładki „Widgety"
 * (sekcja „Domyślny wygląd kart" + nadpisanie per karta) — bliżej samych
 * widgetów. Ten panel zachowuje `widgetStyle` przy zapisie bez modyfikacji.
 *
 * Każda grupa to osobna, płaska sekcja-karta (świadomie BEZ zagnieżdżania jak w
 * SellCall, gdzie panele stylu są nieczytelnie pozagnieżdżane). Zapis przez
 * `useTableLayout(slug)` do `list_config.pageConfig` (PUT — zachowuje kolumny/
 * sort/filtry/quickFilters).
 *
 * Styl paska filtrów (`filterBarStyle`) ma w panelu podgląd na żywo; na liście nakłada go
 * `ListFilterBar` (czyta go z widoku listy `tableId`, gdy strona nie poda go sama).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultPageConfig,
  type FilterBarStyle,
  type PageConfig,
  type RoundingScale,
} from '@saas/shared';
import { Save, Search } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import { useToast } from '@/contexts/ToastContext';

import { useTableLayout } from './useTableLayout';

// Skala zaokrąglenia → px (do podglądu na żywo; runtime mapuje analogicznie).
const ROUNDING_PX: Record<RoundingScale, number> = {
  none: 0,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  '2xl': 16,
};

const ROUNDING_OPTIONS: RoundingScale[] = ['none', 'sm', 'md', 'lg', 'xl', '2xl'];

/** Pole wyboru koloru: natywny picker + hex + reset do domyślnego (motyw). */
function ColorField({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  testId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <span className="block text-xs font-medium text-text-secondary">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value ?? '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-9 cursor-pointer rounded border border-border-secondary bg-transparent p-0.5"
          data-testid={`${testId}-picker`}
          aria-label={label}
        />
        <span className="font-mono text-xs text-text-muted">
          {value ?? t('tenant.listBuilder.page.appearance.themeDefault')}
        </span>
        {value != null && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-xs text-text-tertiary hover:text-text-primary"
            data-testid={`${testId}-reset`}
          >
            {t('tenant.listBuilder.page.appearance.reset')}
          </button>
        )}
      </div>
    </div>
  );
}

export function ListPagePanel({ slug }: { slug: string }): React.ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const { layout, isLoading, saveLayout, isSaving } = useTableLayout(slug);

  const [page, setPage] = useState<PageConfig | null>(null);

  useEffect(() => {
    if (layout?.layoutConfig) {
      setPage({ ...defaultPageConfig, ...(layout.layoutConfig.pageConfig ?? {}) });
    }
  }, [layout]);

  if (isLoading) return <p className="text-sm text-text-tertiary">…</p>;
  if (!layout || !page) {
    return <p className="text-sm text-text-muted">{t('tenant.listBuilder.columns.noLayout')}</p>;
  }

  const setField = <K extends keyof PageConfig>(key: K, value: PageConfig[K]) =>
    setPage((p) => (p ? { ...p, [key]: value } : p));

  const fb: FilterBarStyle = page.filterBarStyle ?? {};

  const setFb = <K extends keyof FilterBarStyle>(key: K, value: FilterBarStyle[K]) =>
    setPage((p) =>
      p ? { ...p, filterBarStyle: { ...(p.filterBarStyle ?? {}), [key]: value } } : p,
    );

  const toggles: Array<{ key: keyof PageConfig; labelKey: string; hintKey: string }> = [
    {
      key: 'showStats',
      labelKey: 'tenant.listBuilder.page.showStats',
      hintKey: 'tenant.listBuilder.page.showStatsHint',
    },
    {
      key: 'showSearch',
      labelKey: 'tenant.listBuilder.page.showSearch',
      hintKey: 'tenant.listBuilder.page.showSearchHint',
    },
    {
      key: 'showFilterBar',
      labelKey: 'tenant.listBuilder.page.showFilterBar',
      hintKey: 'tenant.listBuilder.page.showFilterBarHint',
    },
  ];

  // ── Podgląd: pasek filtrów ──────────────────────────────────────────────
  const blockStyle = fb.blockStyle ?? 'default';
  const previewBlockClass =
    blockStyle === 'card'
      ? 'rounded-lg border-2 border-border-secondary bg-bg-elevated p-3 shadow-md'
      : blockStyle === 'plain'
        ? 'p-1'
        : 'rounded-lg border border-border-secondary bg-bg-elevated p-3';

  const fieldStyle: React.CSSProperties = {
    backgroundColor: fb.fieldBackgroundColor,
    color: fb.fieldTextColor,
    borderColor: fb.fieldBorderColor,
    borderWidth: fb.fieldBorderWidth,
    borderStyle: fb.fieldBorderWidth != null ? 'solid' : undefined,
    borderRadius: fb.fieldRounding != null ? ROUNDING_PX[fb.fieldRounding] : undefined,
  };

  const handleSave = async () => {
    await saveLayout({ ...layout.layoutConfig, pageConfig: page });
    toast.show({ variant: 'success', title: t('tenant.listBuilder.page.saved') });
  };

  const sectionClass = 'space-y-4 rounded-lg border border-border-secondary p-4';
  const headingClass = 'text-sm font-semibold text-text-primary';

  return (
    <div className="space-y-5" data-testid="list-page-panel">
      <p className="text-sm text-text-tertiary">{t('tenant.listBuilder.page.intro')}</p>

      {/* ── Sekcja: Podstawowe ── */}
      <section className={sectionClass} data-testid="list-page-section-basic">
        <h3 className={headingClass}>{t('tenant.listBuilder.page.sectionBasic')}</h3>

        <div className="space-y-1.5">
          <label htmlFor="list-page-title" className="block text-sm font-medium text-text-primary">
            {t('tenant.listBuilder.page.title')}
          </label>
          <input
            id="list-page-title"
            type="text"
            value={page.title ?? ''}
            onChange={(e) => setField('title', e.target.value || undefined)}
            placeholder={t('tenant.listBuilder.page.titlePlaceholder')}
            maxLength={200}
            className="w-full rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary"
            data-testid="list-page-title"
          />
          <p className="text-xs text-text-muted">{t('tenant.listBuilder.page.titleHint')}</p>
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="list-page-description"
            className="block text-sm font-medium text-text-primary"
          >
            {t('tenant.listBuilder.page.description')}
          </label>
          <input
            id="list-page-description"
            type="text"
            value={page.description ?? ''}
            onChange={(e) => setField('description', e.target.value || undefined)}
            placeholder={t('tenant.listBuilder.page.descriptionPlaceholder')}
            maxLength={500}
            className="w-full rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary"
            data-testid="list-page-description"
          />
          <p className="text-xs text-text-muted">{t('tenant.listBuilder.page.descriptionHint')}</p>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="list-page-size" className="block text-sm font-medium text-text-primary">
            {t('tenant.listBuilder.page.pageSize')}
          </label>
          <input
            id="list-page-size"
            type="number"
            min={1}
            max={500}
            value={page.pageSize}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) setField('pageSize', Math.min(500, Math.max(1, n)));
            }}
            className="w-28 rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary"
            data-testid="list-page-size"
          />
          <p className="text-xs text-text-muted">{t('tenant.listBuilder.page.pageSizeHint')}</p>
        </div>
      </section>

      {/* ── Sekcja: Pasek nad listą (przełączniki) ── */}
      <section className={sectionClass} data-testid="list-page-section-toolbar">
        <h3 className={headingClass}>{t('tenant.listBuilder.page.toolbarLegend')}</h3>
        <div className="space-y-3">
          {toggles.map((tg) => (
            <label
              key={tg.key}
              className="flex items-start gap-2 text-sm text-text-primary"
              data-testid={`list-page-toggle-${tg.key}`}
            >
              <input
                type="checkbox"
                checked={page[tg.key] as boolean}
                onChange={(e) => setField(tg.key, e.target.checked as PageConfig[typeof tg.key])}
                className="mt-0.5"
              />
              <span>
                {t(tg.labelKey)}
                <span className="block text-xs text-text-muted">{t(tg.hintKey)}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      {/* ── Sekcja: Wygląd paska wyszukiwania i filtrów ── */}
      <section className={sectionClass} data-testid="list-page-section-filterbar-style">
        <div>
          <h3 className={headingClass}>{t('tenant.listBuilder.page.appearance.filterBarTitle')}</h3>
          <p className="text-xs text-text-muted">
            {t('tenant.listBuilder.page.appearance.filterBarHint')}
          </p>
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="list-page-fb-block"
            className="block text-sm font-medium text-text-primary"
          >
            {t('tenant.listBuilder.page.appearance.blockStyle')}
          </label>
          <Dropdown
            id="list-page-fb-block"
            options={[
              {
                value: 'default',
                label: t('tenant.listBuilder.page.appearance.blockStyles.default'),
              },
              { value: 'card', label: t('tenant.listBuilder.page.appearance.blockStyles.card') },
              { value: 'plain', label: t('tenant.listBuilder.page.appearance.blockStyles.plain') },
            ]}
            value={blockStyle}
            onChange={(val) => setFb('blockStyle', val as FilterBarStyle['blockStyle'])}
            className="w-56 px-2 py-1"
            data-testid="list-page-fb-block"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ColorField
            label={t('tenant.listBuilder.page.appearance.fieldBg')}
            value={fb.fieldBackgroundColor}
            onChange={(v) => setFb('fieldBackgroundColor', v)}
            testId="list-page-fb-bg"
          />
          <ColorField
            label={t('tenant.listBuilder.page.appearance.fieldText')}
            value={fb.fieldTextColor}
            onChange={(v) => setFb('fieldTextColor', v)}
            testId="list-page-fb-text"
          />
          <ColorField
            label={t('tenant.listBuilder.page.appearance.fieldBorder')}
            value={fb.fieldBorderColor}
            onChange={(v) => setFb('fieldBorderColor', v)}
            testId="list-page-fb-border"
          />
          <div className="space-y-1">
            <label
              htmlFor="list-page-fb-bw"
              className="block text-xs font-medium text-text-secondary"
            >
              {t('tenant.listBuilder.page.appearance.fieldBorderWidth')}
              <span className="ml-1 font-mono text-text-muted">{fb.fieldBorderWidth ?? 1}px</span>
            </label>
            <input
              id="list-page-fb-bw"
              type="range"
              min={0}
              max={8}
              value={fb.fieldBorderWidth ?? 1}
              onChange={(e) => setFb('fieldBorderWidth', parseInt(e.target.value, 10))}
              className="w-full"
              data-testid="list-page-fb-bw"
            />
          </div>
          <div className="space-y-1">
            <label
              htmlFor="list-page-fb-round"
              className="block text-xs font-medium text-text-secondary"
            >
              {t('tenant.listBuilder.page.appearance.rounding')}
            </label>
            <Dropdown
              id="list-page-fb-round"
              options={[
                { value: '', label: t('tenant.listBuilder.page.appearance.themeDefault') },
                ...ROUNDING_OPTIONS.map((r) => ({
                  value: r,
                  label: t(`tenant.listBuilder.page.appearance.roundingScale.${r}`),
                })),
              ]}
              value={fb.fieldRounding ?? ''}
              onChange={(val) => setFb('fieldRounding', val ? (val as RoundingScale) : undefined)}
              className="w-40 px-2 py-1"
              data-testid="list-page-fb-round"
            />
          </div>
        </div>

        {/* Podgląd na żywo */}
        <div className="space-y-1.5">
          <span className="block text-xs font-medium text-text-secondary">
            {t('tenant.listBuilder.page.appearance.preview')}
          </span>
          <div className={previewBlockClass} data-testid="list-page-fb-preview">
            <div className="flex flex-wrap items-center gap-2">
              <div
                className="flex min-w-[200px] flex-1 items-center gap-2 border border-border-secondary bg-bg-primary px-3 py-1.5"
                style={fieldStyle}
              >
                <Search size={14} aria-hidden="true" />
                <span className="text-sm opacity-70">
                  {t('tenant.listBuilder.page.appearance.previewSearch')}
                </span>
              </div>
              <div
                className="border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm"
                style={fieldStyle}
              >
                {t('tenant.listBuilder.page.appearance.previewFilter')}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
          data-testid="list-page-save"
        >
          <Save size={16} aria-hidden="true" />
          {isSaving ? t('tenant.listBuilder.page.saving') : t('tenant.listBuilder.page.saveBtn')}
        </button>
      </div>
    </div>
  );
}
